import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Award, TrendingUp, Gift, History } from "lucide-react";

const TIER_COLORS = {
  Bronze: "#CD7F32",
  Silver: "#A8A8A8",
  Gold: "#D4AF37",
  Platinum: "#B5A98E",
};

const LoyaltyCard = () => {
  const { lang, t } = useLang();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [tiersInfo, setTiersInfo] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    api.get("/loyalty/me").then((r) => setData(r.data)).catch(() => {});
    api.get("/loyalty/tiers").then((r) => setTiersInfo(r.data)).catch(() => {});
  }, []);

  const loadHistory = () => {
    api.get("/loyalty/history").then((r) => setHistory(r.data || [])).catch(() => {});
  };

  if (!data) return null;

  const tierColor = TIER_COLORS[data.tier] || "#D4AF37";
  // Compute true tier-relative progress using tiers ladder when available
  const computeProgress = () => {
    if (!data.next_tier || !tiersInfo?.tiers) return 100;
    const tiers = tiersInfo.tiers;
    const currentIdx = tiers.findIndex((t) => t.name === data.tier);
    const nextIdx = currentIdx + 1;
    if (currentIdx < 0 || nextIdx >= tiers.length) return 100;
    const floor = tiers[currentIdx].min;
    const ceiling = tiers[nextIdx].min;
    const span = ceiling - floor;
    if (span <= 0) return 100;
    return Math.min(100, Math.max(0, ((data.points - floor) / span) * 100));
  };
  const progress = computeProgress();

  return (
    <section className="bg-gradient-to-br from-[#2C1E16] to-[#3D2A1F] text-white p-8 lg:p-10 relative overflow-hidden" data-testid="loyalty-card">
      <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full opacity-10" style={{ background: tierColor }} />
      <div className="absolute -bottom-12 -left-12 w-32 h-32 rounded-full opacity-10" style={{ background: tierColor }} />

      <div className="relative">
        <div className="flex items-center gap-3 mb-2">
          <Award size={22} style={{ color: tierColor }} />
          <p className="overline" style={{ color: tierColor }}>{t("loyalty.title")}</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 mt-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-white/60 mb-1">{t("loyalty.yourPoints")}</p>
            <p className="font-serif text-5xl lg:text-6xl" style={{ color: tierColor }} data-testid="loyalty-points">
              {data.points.toLocaleString()}
            </p>
            <p className="text-xs text-white/50 mt-1">{t("loyalty.points")}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-white/60 mb-1">{t("loyalty.tier")}</p>
            <p className="font-serif text-3xl lg:text-4xl mb-2" style={{ color: tierColor }} data-testid="loyalty-tier">
              {data.tier}
            </p>
            <p className="text-xs text-white/70 leading-relaxed">{data.tier_perks}</p>
          </div>
        </div>

        {data.next_tier && (
          <div className="mt-8" data-testid="loyalty-progress">
            <div className="flex justify-between text-xs text-white/60 mb-2">
              <span>{data.points_to_next_tier} {t("loyalty.pointsToNext")} {data.next_tier}</span>
              <span>{data.next_tier}</span>
            </div>
            <div className="h-1.5 bg-white/10 overflow-hidden">
              <div className="h-full transition-all duration-700" style={{ width: `${progress}%`, background: tierColor }} />
            </div>
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-white/10 grid sm:grid-cols-3 gap-4 text-xs">
          <div className="flex items-start gap-3">
            <TrendingUp size={16} style={{ color: tierColor }} className="mt-0.5" />
            <span className="text-white/80">{t("loyalty.earn")}</span>
          </div>
          <div className="flex items-start gap-3">
            <Gift size={16} style={{ color: tierColor }} className="mt-0.5" />
            <span className="text-white/80">{t("loyalty.redeem")}</span>
          </div>
          <div className="flex items-start gap-3">
            <Award size={16} style={{ color: tierColor }} className="mt-0.5" />
            <span className="text-white/80">{t("loyalty.minRedeem")}</span>
          </div>
        </div>

        <button
          onClick={() => { setShowHistory((s) => !s); if (!showHistory) loadHistory(); }}
          className="mt-6 inline-flex items-center gap-2 text-xs tracking-widest uppercase text-white/70 hover:text-white transition"
          data-testid="toggle-loyalty-history"
        >
          <History size={14} /> {t("loyalty.history")}
        </button>

        {showHistory && (
          <div className="mt-4 max-h-64 overflow-y-auto pr-2" data-testid="loyalty-history">
            {history.length === 0 ? (
              <p className="text-xs text-white/50 py-4">{t("loyalty.noHistory")}</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {history.map((tx) => (
                    <tr key={tx.id} className="border-b border-white/5">
                      <td className="py-2.5">
                        <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold ${tx.type === "earn" ? "bg-emerald-900/40 text-emerald-300" : "bg-amber-900/40 text-amber-300"}`}>
                          {tx.type === "earn" ? t("loyalty.earned") : t("loyalty.redeemed")}
                        </span>
                      </td>
                      <td className="py-2.5 text-white/70 text-xs">
                        {new Date(tx.created_at).toLocaleDateString(lang === "el" ? "el-GR" : "en-US")}
                      </td>
                      <td className="py-2.5 text-right font-mono" style={{ color: tx.points > 0 ? "#10b981" : "#f59e0b" }}>
                        {tx.points > 0 ? "+" : ""}{tx.points}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default LoyaltyCard;
